import {
  CreateCustomRequest,
  CreateCustomResponse,
  CreateSFTPRequest,
  CreateSFTPResponse,
  CustomService,
  SftpService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const sftpFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  sftpHost: z.string().min(2, 'SFTP host must be at least 2 characters.'),
  sftpUsername: z
    .string()
    .min(2, 'SFTP username must be at least 2 characters.'),
  sftpPassword: z.string().optional(),
  sftpPrivateKey: z.string().optional(),
  sftpPublicKey: z.string().optional(),
});

const customFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  customHost: z.string().min(2, 'Custom Host must be at least 2 characters.'),
  customUsername: z
    .string()
    .min(2, 'Custom Username must be at least 2 characters.'),
  customPassword: z.string().optional(),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  organizationId: string;
}

export function NewIntegrationModal({ open, setOpen, organizationId }: Props) {
  const queryClient = useQueryClient();
  const [selectedIntegration, setSelectedIntegration] = useState<
    'SFTP' | 'Custom' | null
  >(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [isTestSuccessful, setIsTestSuccessful] = useState(false);

  const handleIntegrationChange = (integration: 'SFTP' | 'Custom') => {
    setSelectedIntegration(integration);
    setTestResult(null);
    setIsTestSuccessful(false);
  };

  const sftpForm = useForm<z.infer<typeof sftpFormSchema>>({
    resolver: zodResolver(sftpFormSchema),
    defaultValues: {
      name: '',
      sftpHost: '',
      sftpUsername: '',
      sftpPassword: '',
      sftpPrivateKey: '',
      sftpPublicKey: '',
    },
  });

  const customForm = useForm<z.infer<typeof customFormSchema>>({
    resolver: zodResolver(customFormSchema),
    defaultValues: {
      name: '',
      customHost: '',
      customUsername: '',
      customPassword: '',
    },
  });

  const createSFTPIntegration = useMutation<
    CreateSFTPResponse,
    Error,
    CreateSFTPRequest
  >({
    mutationFn: payload => SftpService.createSftp(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getIntegrations', organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getOrgIntegrations', organizationId],
      });
      setOpen(false);
      sftpForm.reset();
      toast.success('SFTP Integration created successfully!');
    },
    onError: error => {
      console.error(error);
      toast.error('An error occurred while creating the integration.', {
        description: error.message,
      });
    },
  });

  const createCustomIntegration = useMutation<
    CreateCustomResponse,
    Error,
    CreateCustomRequest
  >({
    mutationFn: payload => CustomService.createCustom(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getIntegrations', organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getOrgIntegrations', organizationId],
      });
      setOpen(false);
      customForm.reset();
      toast.success('Custom Integration created successfully!');
    },
    onError: error => {
      console.error(error);
      toast.error('An error occurred while creating the integration.', {
        description: error.message,
      });
    },
  });

  const onSubmitSFTP = (values: z.infer<typeof sftpFormSchema>) => {
    createSFTPIntegration.mutate({ ...values, orgId: organizationId });
  };

  const onSubmitCustom = (values: z.infer<typeof customFormSchema>) => {
    createCustomIntegration.mutate({ ...values, orgId: organizationId });
  };
  const testSFTPConnection = async (values: z.infer<typeof sftpFormSchema>) => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await SftpService.testSftpConnection({
        sftpHost: values.sftpHost,
        sftpUsername: values.sftpUsername,
        sftpPassword: values.sftpPassword || '',
      });
      setTestResult('SFTP connection successful!');
      setIsTestSuccessful(true);
      toast.success('SFTP connection successful!');
    } catch (error) {
      setTestResult('SFTP connection failed. Check credentials.');
      setIsTestSuccessful(false);
      toast.error('SFTP connection failed.');
    } finally {
      setIsTesting(false);
    }
  };

  const testCustomConnection = async (
    values: z.infer<typeof customFormSchema>,
  ) => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await CustomService.testCustomConnection({
        customHost: values.customHost,
        customUsername: values.customUsername,
        customPassword: values.customPassword || '',
      });
      setTestResult('Custom connection successful!');
      setIsTestSuccessful(true);
      toast.success('Custom connection successful!');
    } catch (error) {
      setTestResult('Custom connection failed. Check credentials.');
      setIsTestSuccessful(false);
      toast.error('Custom connection failed.');
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        sftpForm.reset();
        customForm.reset();
        setOpen(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Integration</DialogTitle>
          <DialogDescription>
            Select and configure a new integration for your organization.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">
            Select Integration Type
          </label>
          <div className="mt-2 flex space-x-4">
            <Button
              variant={selectedIntegration === 'SFTP' ? 'default' : 'outline'}
              onClick={() => handleIntegrationChange('SFTP')}
            >
              SFTP
            </Button>
            <Button
              variant={selectedIntegration === 'Custom' ? 'default' : 'outline'}
              onClick={() => handleIntegrationChange('Custom')}
            >
              Custom
            </Button>
          </div>
        </div>

        {selectedIntegration === 'SFTP' && (
          <Form {...sftpForm}>
            <form
              onSubmit={sftpForm.handleSubmit(onSubmitSFTP)}
              className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2"
            >
              <FormField
                control={sftpForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Integration Name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={sftpForm.control}
                name="sftpHost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Host" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={sftpForm.control}
                name="sftpUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Username</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Username" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={sftpForm.control}
                name="sftpPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SFTP Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Enter SFTP Password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={sftpForm.control}
                name="sftpPrivateKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Private Key</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Private Key" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={sftpForm.control}
                name="sftpPublicKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Public Key</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter SFTP Public Key" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="col-span-1 flex justify-end space-x-4 md:col-span-2">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => testSFTPConnection(sftpForm.getValues())}
                  disabled={isTesting}
                >
                  {isTesting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    'Test'
                  )}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !isTestSuccessful || createSFTPIntegration.isPending
                  }
                >
                  {createSFTPIntegration.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    'Create'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}

        {selectedIntegration === 'Custom' && (
          <Form {...customForm}>
            <form
              onSubmit={customForm.handleSubmit(onSubmitCustom)}
              className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2"
            >
              <FormField
                control={customForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Integration Name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={customForm.control}
                name="customHost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Host</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Custom Host" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={customForm.control}
                name="customUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Username</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter Custom Username" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={customForm.control}
                name="customPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Enter Custom Password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="col-span-1 flex justify-end space-x-4 md:col-span-2">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>

                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => testCustomConnection(customForm.getValues())}
                  disabled={isTesting}
                >
                  {isTesting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    'Test'
                  )}
                </Button>
                <Button
                  type="submit"
                  disabled={
                  createCustomIntegration.isPending
                  }
                >
                  {createCustomIntegration.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    'Create'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
